// "Use my current location" button + its failure message (DEV-51044). Shared
// by the postcode screen and the Change Location drawer.
//
// Deliberately secondary weight: this only fills the postcode field, it never
// confirms a location. The primary button on each screen still does that.

import clsx from 'clsx'
import { Loader2, LocateFixed } from 'lucide-react'

import { Button } from '../radix'
import type { DeviceLocationFailure } from '../../services/deviceLocation'
import type { DeviceLocationStatus } from '../../hooks/useDeviceLocation'

// Each message ends by pointing the engineer back to the postcode field, which
// keeps working in every failure case.
const FAILURE_MESSAGES: Record<DeviceLocationFailure, string> = {
  denied:
    'Location is switched off for this browser. Enter a postcode instead.',
  unavailable: "We couldn't get your location. Enter a postcode instead.",
  'no-postcode':
    "We couldn't find a UK postcode where you are. Enter a postcode instead.",
}

export interface UseMyLocationButtonProps {
  /** Renders nothing when false — a button that can only fail is worse than none. */
  isSupported: boolean
  status: DeviceLocationStatus
  failureReason: DeviceLocationFailure | null
  onLocate: () => void
}

export function UseMyLocationButton({
  isSupported,
  status,
  failureReason,
  onLocate,
}: UseMyLocationButtonProps) {
  if (!isSupported) {
    return null
  }

  const isLocating = status === 'locating'

  return (
    <div>
      <Button
        type="button"
        variant="outline"
        size="md"
        // aria-disabled rather than `disabled`: a disabled button loses focus,
        // which drops the engineer out of the form mid-task. The click handler
        // ignores repeat taps instead.
        aria-disabled={isLocating}
        aria-busy={isLocating}
        onClick={() => {
          if (!isLocating) {
            onLocate()
          }
        }}
        // Not full width: this fills the field, it doesn't confirm. A
        // full-width outline button competes with the primary one below.
        className={clsx(isLocating && 'opacity-60')}
      >
        {isLocating ? (
          <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />
        ) : (
          <LocateFixed className="w-4 h-4" aria-hidden="true" />
        )}
        {isLocating ? 'Finding your location…' : 'Use my current location'}
      </Button>

      <div aria-live="polite">
        {status === 'failed' && failureReason && (
          <p className="mt-2 text-sm text-gray-700">
            {FAILURE_MESSAGES[failureReason]}
          </p>
        )}
      </div>
    </div>
  )
}
